import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import {
  Plus,
  Users,
  Search,
  Mail,
  Building,
  User,
  FileCheck,
  MoreVertical,
  Upload,
  Edit,
  Send,
  Trash2,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';

const statusColors = {
  invited: 'bg-amber-500/10 text-amber-400 border border-amber-500/20',
  active: 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20',
  inactive: 'bg-zinc-500/10 text-zinc-400 border border-zinc-500/20',
};

export default function Vendors() {
  const { api } = useAuth();
  const [searchParams] = useSearchParams();
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [w9ModalOpen, setW9ModalOpen] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [resendingEmail, setResendingEmail] = useState(null);

  const [inviteForm, setInviteForm] = useState({
    email: '',
    company_name: '',
    contact_name: '',
  });

  const [editForm, setEditForm] = useState({
    email: '',
    company_name: '',
    contact_name: '',
  });

  useEffect(() => {
    fetchVendors();
    if (searchParams.get('action') === 'invite') {
      setInviteModalOpen(true);
    }
  }, []);

  const fetchVendors = async () => {
    try {
      const response = await api().get('/vendors');
      setVendors(response.data);
    } catch (error) {
      toast.error('Failed to load vendors');
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    try {
      await api().post('/vendors/invite', inviteForm);
      toast.success('Vendor invited successfully');
      setInviteModalOpen(false);
      setInviteForm({ email: '', company_name: '', contact_name: '' });
      fetchVendors();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to invite vendor');
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    try {
      await api().put(`/vendors/${selectedVendor.id}`, editForm);
      toast.success('Vendor updated successfully');
      setEditModalOpen(false);
      setSelectedVendor(null);
      fetchVendors();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update vendor');
    }
  };

  const handleResendInvite = async (vendor) => {
    setResendingEmail(vendor.id);
    try {
      await api().post(`/vendors/${vendor.id}/resend-invite`);
      toast.success(`Invitation resent to ${vendor.email}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend invitation');
    } finally {
      setResendingEmail(null);
    }
  };

  const openEditModal = (vendor) => {
    setSelectedVendor(vendor);
    setEditForm({
      email: vendor.email,
      company_name: vendor.company_name,
      contact_name: vendor.contact_name,
    });
    setEditModalOpen(true);
  };

  const handleW9Upload = async (e) => {
    e.preventDefault();
    const fileInput = document.getElementById('w9-file');
    const file = fileInput.files[0];

    if (!file) {
      toast.error('Please select a file');
      return;
    }

    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const payload = {
          file_data: reader.result,
          file_name: file.name,
          file_type: file.type
        };
        
        await api().post(`/vendors/${selectedVendor.id}/w9`, payload);
        toast.success('W-9 uploaded successfully');
        setW9ModalOpen(false);
        setSelectedVendor(null);
        fetchVendors();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to upload W-9');
      }
    };
    reader.readAsDataURL(file);
  };

  const filteredVendors = vendors.filter(
    (v) =>
      v.company_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      v.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      v.contact_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="space-y-6 animate-fade-in">
        <div className="h-8 w-48 skeleton" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-40 skeleton rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="vendors-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white font-['Manrope'] tracking-tight">
            Vendors
          </h1>
          <p className="text-zinc-400 mt-1">
            Manage your vendor relationships
          </p>
        </div>
        <Dialog open={inviteModalOpen} onOpenChange={setInviteModalOpen}>
          <DialogTrigger asChild>
            <Button
              className="bg-blue-600 hover:bg-blue-500 shadow-[0_0_15px_rgba(59,130,246,0.4)]"
              data-testid="invite-vendor-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Invite Vendor
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-zinc-900 border-zinc-800">
            <DialogHeader>
              <DialogTitle className="text-white">Invite Vendor</DialogTitle>
              <DialogDescription className="text-zinc-400">
                Send an invitation to a new vendor
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleInvite} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-zinc-300">Email Address</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <Input
                    type="email"
                    value={inviteForm.email}
                    onChange={(e) =>
                      setInviteForm({ ...inviteForm, email: e.target.value })
                    }
                    placeholder="vendor@company.com"
                    className="bg-zinc-950 border-zinc-800 pl-10"
                    required
                    data-testid="vendor-email-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">Company Name</Label>
                <div className="relative">
                  <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <Input
                    value={inviteForm.company_name}
                    onChange={(e) =>
                      setInviteForm({ ...inviteForm, company_name: e.target.value })
                    }
                    placeholder="Vendor Company Inc."
                    className="bg-zinc-950 border-zinc-800 pl-10"
                    required
                    data-testid="vendor-company-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">Contact Name</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <Input
                    value={inviteForm.contact_name}
                    onChange={(e) =>
                      setInviteForm({ ...inviteForm, contact_name: e.target.value })
                    }
                    placeholder="John Smith"
                    className="bg-zinc-950 border-zinc-800 pl-10"
                    required
                    data-testid="vendor-contact-input"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setInviteModalOpen(false)}
                  className="border-zinc-700"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-blue-600 hover:bg-blue-500"
                  data-testid="send-invite-btn"
                >
                  Send Invitation
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
        <Input
          placeholder="Search vendors..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9 bg-zinc-900 border-zinc-800"
          data-testid="vendor-search-input"
        />
      </div>

      {/* Vendors Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredVendors.length > 0 ? (
          filteredVendors.map((vendor) => (
            <Card
              key={vendor.id}
              className="bg-zinc-900/40 border-zinc-800 card-hover"
              data-testid={`vendor-card-${vendor.id}`}
            >
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-zinc-800 flex items-center justify-center">
                      <Building className="w-5 h-5 text-zinc-400" />
                    </div>
                    <div>
                      <CardTitle className="text-lg text-zinc-200">
                        {vendor.company_name}
                      </CardTitle>
                      <p className="text-sm text-zinc-500">{vendor.contact_name}</p>
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      align="end"
                      className="bg-zinc-900 border-zinc-800"
                    >
                      <DropdownMenuItem
                        onClick={() => openEditModal(vendor)}
                        className="text-zinc-300"
                      >
                        <Edit className="w-4 h-4 mr-2" />
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => handleResendInvite(vendor)}
                        className="text-zinc-300"
                        disabled={resendingEmail === vendor.id}
                      >
                        <Send className="w-4 h-4 mr-2" />
                        {resendingEmail === vendor.id ? 'Sending...' : 'Resend Invite'}
                      </DropdownMenuItem>
                      {!vendor.w9_uploaded && (
                        <DropdownMenuItem
                          onClick={() => {
                            setSelectedVendor(vendor);
                            setW9ModalOpen(true);
                          }}
                          className="text-zinc-300"
                        >
                          <Upload className="w-4 h-4 mr-2" />
                          Upload W-9
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-2 text-sm text-zinc-400">
                  <Mail className="w-4 h-4" />
                  {vendor.email}
                </div>
                <div className="flex items-center justify-between">
                  <Badge className={statusColors[vendor.status]}>
                    {vendor.status}
                  </Badge>
                  {vendor.w9_uploaded ? (
                    <span className="flex items-center gap-1 text-xs text-emerald-400">
                      <FileCheck className="w-3 h-3" />
                      W-9 on file
                    </span>
                  ) : (
                    <span className="text-xs text-amber-400">W-9 pending</span>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        ) : (
          <Card className="col-span-full bg-zinc-900/40 border-zinc-800">
            <CardContent>
              <div className="empty-state">
                <Users className="empty-state-icon" />
                <p className="text-zinc-400">No vendors found</p>
                <p className="text-zinc-500 text-sm">
                  Invite your first vendor to get started
                </p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* W-9 Upload Modal */}
      <Dialog open={w9ModalOpen} onOpenChange={setW9ModalOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="text-white">Upload W-9</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Upload W-9 form for {selectedVendor?.company_name}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleW9Upload} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-zinc-300">W-9 Document</Label>
              <Input
                id="w9-file"
                type="file"
                accept=".pdf,.doc,.docx,.png,.jpg"
                className="bg-zinc-950 border-zinc-800 text-zinc-300"
                required
              />
              <p className="text-xs text-zinc-500">
                Accepted formats: PDF, DOC, DOCX, PNG, JPG
              </p>
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setW9ModalOpen(false)}
                className="border-zinc-700"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-blue-600 hover:bg-blue-500"
                data-testid="upload-w9-btn"
              >
                <Upload className="w-4 h-4 mr-2" />
                Upload
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Vendor Modal */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="text-white">Edit Vendor</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Update vendor information for {selectedVendor?.company_name}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEdit} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-zinc-300">Email Address</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input
                  type="email"
                  value={editForm.email}
                  onChange={(e) =>
                    setEditForm({ ...editForm, email: e.target.value })
                  }
                  placeholder="vendor@company.com"
                  className="bg-zinc-950 border-zinc-800 pl-10"
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">Company Name</Label>
              <div className="relative">
                <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input
                  value={editForm.company_name}
                  onChange={(e) =>
                    setEditForm({ ...editForm, company_name: e.target.value })
                  }
                  placeholder="Vendor Company Inc."
                  className="bg-zinc-950 border-zinc-800 pl-10"
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">Contact Name</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input
                  value={editForm.contact_name}
                  onChange={(e) =>
                    setEditForm({ ...editForm, contact_name: e.target.value })
                  }
                  placeholder="John Smith"
                  className="bg-zinc-950 border-zinc-800 pl-10"
                  required
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setEditModalOpen(false)}
                className="border-zinc-700"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-blue-600 hover:bg-blue-500"
              >
                Update Vendor
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
